import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Lock, Unlock, Send, Loader2, Eye, Zap, Target, TrendingUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const revenueRanges = [
  { id: 'rev-empty', value: '', label: 'Select your monthly revenue...' },
  { id: 'rev-pre', value: 'pre_revenue', label: 'Pre-revenue / Just starting' },
  { id: 'rev-0-10k', value: '0_10k', label: '$0 - $10,000' },
  { id: 'rev-10k-50k', value: '10k_50k', label: '$10,000 - $50,000' },
  { id: 'rev-50k-100k', value: '50k_100k', label: '$50,000 - $100,000' },
  { id: 'rev-100k-500k', value: '100k_500k', label: '$100,000 - $500,000' },
  { id: 'rev-500k-plus', value: '500k_plus', label: '$500,000+' },
];

const teaserItems = [
  { id: 'teaser-traffic', icon: Eye, title: 'Traffic Generation', description: 'Learn the 5 proven channels that drive qualified leads' },
  { id: 'teaser-capture', icon: Target, title: 'Lead Capture Systems', description: 'Discover how to convert visitors into leads automatically' },
  { id: 'teaser-automation', icon: Zap, title: 'Automation Sequences', description: 'See the exact email flows that nurture prospects' },
  { id: 'teaser-conversion', icon: TrendingUp, title: 'Conversion Strategies', description: 'Unlock the secrets to predictable revenue growth' },
];

// Teaser card component
function TeaserCard({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 + index * 0.1 }}
      className="group relative"
    >
      <div className="glass rounded-xl p-5 border border-white/10 hover:border-cyan-500/30 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan-500/20 transition-colors">
            <item.icon className="w-5 h-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">{item.title}</h4>
            <p className="text-gray-400 text-sm">{item.description}</p>
          </div>
          <Lock className="w-4 h-4 text-gray-600 flex-shrink-0" />
        </div>
      </div>
    </motion.div>
  );
}

// Lead capture form component
function LeadCaptureForm({ onSubmit, isSubmitting }) {
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    company: '',
    revenue_range: '',
  });

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isFormValid = formData.first_name && formData.email && formData.revenue_range;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-400 mb-2">First Name *</label>
        <input
          data-testid="gate-first-name"
          type="text"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
          required
          className="input-dark w-full"
          placeholder="Your first name"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
        <input
          data-testid="gate-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="input-dark w-full"
          placeholder="you@company.com"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Company / Website</label>
        <input
          data-testid="gate-company"
          type="text"
          name="company"
          value={formData.company}
          onChange={handleChange}
          className="input-dark w-full"
          placeholder="Your company or website URL"
        />
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Monthly Revenue Range *</label>
        <select
          data-testid="gate-revenue"
          name="revenue_range"
          value={formData.revenue_range}
          onChange={handleChange}
          required
          className="input-dark w-full"
        >
          {revenueRanges.map((range) => (
            <option key={range.id} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>
      </div>

      <button
        data-testid="gate-submit"
        type="submit"
        disabled={isSubmitting || !isFormValid}
        className="btn-primary w-full flex items-center justify-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Unlocking...</span>
          </>
        ) : (
          <>
            <Unlock className="w-5 h-5" />
            <span>Unlock The Guide</span>
          </>
        )}
      </button>

      <p className="text-center text-gray-500 text-xs mt-4">
        🔒 Your information is secure and will never be shared
      </p>
    </form>
  );
}

export default function GateSection({ onUnlock }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = useCallback(async (formData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: 'Guide Access',
          email: formData.email,
          company: formData.company,
          message: `Revenue Range: ${formData.revenue_range}`,
          service_interest: 'funnel_guide_access'
        }),
      });

      if (response.ok) {
        toast.success('Access Granted!', { description: 'Enjoy your complete funnel guide.' });
        onUnlock();
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission error:', error);
      }
      toast.error('Something went wrong', { description: 'Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }, [onUnlock]);

  return (
    <section 
      id="unlock-guide"
      data-testid="gate-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#030303] via-[#050a0a] to-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,240,255,0.08)_0%,_transparent_60%)]" />
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs uppercase tracking-widest text-cyan-400 mb-6">
            <Lock className="w-3 h-3" />
            Exclusive Content
          </div>
          
          <h2 data-testid="gate-headline" className="text-4xl md:text-5xl font-bold text-white mb-6">
            Unlock The Complete
            <br />
            <span className="gradient-text-cyan">Funnel Guide</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            Get instant access to the full interactive guide showing exactly how 
            a marketing funnel transforms traffic into revenue.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-start">
          {/* Left: Teaser Preview */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Eye className="w-5 h-5 text-cyan-400" />
              What You'll Discover Inside
            </h3>
            
            <div className="space-y-4">
              {teaserItems.map((item, index) => (
                <TeaserCard key={item.id} item={item} index={index} />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="mt-6 p-6 rounded-xl bg-white/5 border border-white/10 relative overflow-hidden"
            >
              <div className="absolute inset-0 backdrop-blur-md bg-black/40 flex items-center justify-center z-10">
                <div className="text-center">
                  <Lock className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Complete guide locked</p>
                </div>
              </div>
              <div className="blur-sm">
                <div className="h-4 bg-gray-700 rounded w-3/4 mb-3" />
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-3" />
                <div className="h-4 bg-gray-700 rounded w-2/3" />
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Lead Capture Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <div className="glass-heavy rounded-2xl p-8 border border-cyan-500/20">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <Unlock className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Get Instant Access</h3>
                  <p className="text-gray-400 text-sm">Free • No credit card required</p>
                </div>
              </div>

              <LeadCaptureForm onSubmit={handleFormSubmit} isSubmitting={isSubmitting} />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="flex justify-center mt-16"
        >
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-3">Enter your details to continue</p>
            <ArrowDown className="w-5 h-5 text-cyan-400 mx-auto animate-bounce" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
